import React, { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cleanupAdsense } from '@/lib/adsenseCleanup';
import { tierHasFeature, FeatureKey } from '@/config/tiers';
import { 
  saveOfflineSession, 
  getOfflineSession, 
  clearOfflineSession,
  updateLastOnlineSync,
  getOfflineSessionDaysRemaining,
  type OfflineSessionData 
} from '@/lib/offlineSession';
import { createDebouncedOfflineHandler, checkConnectivity } from '@/lib/connectivityCheck';
interface CreditBalance {
  isUnlimited: boolean;
  tier: 'free' | 'pro' | 'chef_master';
  tierLimit: number;
  creditsUsed: number;
  freeRemaining: number;
  purchasedCredits: number;
  totalAvailable: number;
  isTrialCredits: boolean;
  trialCreditsExpireAt: string | null;
}

interface TrialStatus {
  isOnTrial: boolean;
  trialTier: 'chef_master' | 'pro' | null;
  trialStartDate: string | null;
  trialEndDate: string | null;
  daysRemaining: number;
  trialUsedChefMaster: boolean;
  trialUsedPro: boolean;
  isEligibleForTrial: boolean; // New: can start a Stripe trial
  canTry24HourPro: boolean; // Can start 24-hour no-card Pro trial
  canTry24HourChefMaster: boolean; // Can start 24-hour no-card Chef Master trial
  // Stripe card trial tracking (for tier upgrade prompt)
  isOnStripeCardTrial: boolean;
  stripeTrialDaysUsed: number;
  canUpgradeTrialTier: boolean; // Show upgrade prompt? (Pro trial after day 4)
}

interface SubscriptionContextType {
  tier: 'free' | 'pro' | 'chef_master';
  effectiveTier: 'free' | 'pro' | 'chef_master';
  loading: boolean;
  hasFeature: (feature: string) => boolean;
  creditBalance: CreditBalance;
  refetchSubscription: () => void;
  manualSyncSubscription: () => Promise<void>;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean | null;
  stripeCustomerId: string | null;
  // Trial support
  trial: TrialStatus;
  // Ad control - backend source of truth
  adsAllowed: boolean;
  // Offline session support
  isOfflineMode: boolean;
  offlineSessionDaysRemaining: number;
  // Family sharing
  isFamilyMember: boolean;
  // Legacy support
  remainingUsage: {
    chaosKitchen: { daily: number; monthly: number; };
    cursedMode: { daily: number; monthly: number; };
    recipeTransform: { daily: number; monthly: number; };
    photoAnalysis: { daily: number; monthly: number; };
    purchasedCredits: number;
    unlimited: boolean;
  };
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

// CRITICAL: Detect print route at MODULE LEVEL before any component renders
// This prevents race conditions with connectivity checks in new browser tabs
const PRINT_ROUTE_PATTERN = /\/print\/?$/;
const isOnPrintRouteAtLoad = typeof window !== 'undefined' && PRINT_ROUTE_PATTERN.test(window.location.pathname);

// Also export for use in other files
export const isPrintRoute = () => {
  if (typeof window !== 'undefined') {
    return PRINT_ROUTE_PATTERN.test(window.location.pathname);
  }
  return false;
};

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [tier, setTier] = useState<'free' | 'pro' | 'chef_master'>('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean | null>(null);
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
  
  // Use module-level print route detection for this instance
  // This is already evaluated at module load, so no re-render race conditions
  const isOnPrintRoute = isOnPrintRouteAtLoad;
  
  // Offline session state
  // CRITICAL: For print routes, always start as ONLINE to prevent blocking
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [offlineSession, setOfflineSession] = useState<OfflineSessionData | null>(null);
  const [offlineSessionDaysRemaining, setOfflineSessionDaysRemaining] = useState(0);
  const [offlineSessionChecked, setOfflineSessionChecked] = useState(false);
  // For print routes, skip connectivity check entirely
  const [connectivityChecked, setConnectivityChecked] = useState(isOnPrintRoute);

  // Initial connectivity check on mount
  // Skip for print routes - they handle their own loading and don't need offline protection
  useEffect(() => {
    // If already checked (print route), skip
    if (connectivityChecked) {
      console.log('[SubscriptionContext] Connectivity already checked (print route bypass)');
      return;
    }
    
    const verifyInitialConnectivity = async () => {
      // Quick check first
      if (!navigator.onLine) {
        console.log('[SubscriptionContext] Browser reports offline');
        setIsOfflineMode(true);
        setConnectivityChecked(true);
        return;
      }
      
      // Verify with actual request
      const isConnected = await checkConnectivity();
      console.log('[SubscriptionContext] Initial connectivity check:', isConnected ? 'online' : 'offline');
      setIsOfflineMode(!isConnected);
      setConnectivityChecked(true);
    };
    
    verifyInitialConnectivity();
  }, [connectivityChecked]);

  // Listen for online/offline changes with debounce
  useEffect(() => {
    const { handleOnline, handleOffline, cleanup } = createDebouncedOfflineHandler(
      // onOffline
      () => {
        console.log('[SubscriptionContext] Confirmed offline');
        setIsOfflineMode(true);
      },
      // onOnline
      () => {
        console.log('[SubscriptionContext] Confirmed online');
        setIsOfflineMode(false);
        // Update last sync time when coming online
        updateLastOnlineSync();
      }
    );

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      cleanup();
    };
  }, []);

  // Check for existing offline session on mount
  useEffect(() => {
    const checkOfflineSession = async () => {
      try {
        const session = await getOfflineSession();
        if (session) {
          setOfflineSession(session);
          const daysRemaining = await getOfflineSessionDaysRemaining();
          setOfflineSessionDaysRemaining(daysRemaining);
          console.log('[SubscriptionContext] Found cached offline session:', {
            tier: session.effectiveTier,
            daysRemaining,
          });
        }
      } catch (error) {
        console.error('[SubscriptionContext] Error checking offline session:', error);
      } finally {
        setOfflineSessionChecked(true);
      }
    };
    checkOfflineSession();
  }, []);

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    // Only fetch when online
    enabled: !isOfflineMode,
  });

  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('user_subscriptions')
        .select('tier, subscription_status, current_period_end, current_period_start, cancel_at_period_end, stripe_customer_id, stripe_subscription_id, trial_tier, trial_start_date, trial_end_date, trial_used_chef_master, trial_used_pro, has_been_paid_subscriber, halloween_lifetime_pro')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id && !isOfflineMode,
  });

  // Check family membership
  const { data: familyMembershipData, isLoading: familyLoading } = useQuery({
    queryKey: ['family-membership', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('family_members')
        .select('id, status, family_group_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id && !isOfflineMode,
  });

  // Real-time subscription updates - instant unlock after Stripe webhook processes
  useEffect(() => {
    if (!user?.id || isOfflineMode) return;

    console.log('[SubscriptionContext] Setting up realtime subscription listener');
    
    const channel = supabase
      .channel('subscription-updates')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'user_subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[SubscriptionContext] Realtime subscription update received:', payload);
          // Immediately refetch all subscription-related data
          queryClient.invalidateQueries({ queryKey: ['subscription'] });
          queryClient.invalidateQueries({ queryKey: ['credit-balance'] });
          queryClient.invalidateQueries({ queryKey: ['trial-status'] });
        }
      )
      .subscribe((status) => {
        console.log('[SubscriptionContext] Realtime channel status:', status);
      });

    return () => {
      console.log('[SubscriptionContext] Cleaning up realtime subscription listener');
      supabase.removeChannel(channel);
    };
  }, [user?.id, isOfflineMode, queryClient]);

  // Fetch trial status
  const { data: trialStatusData, isLoading: trialLoading } = useQuery({
    queryKey: ['trial-status', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase.rpc('get_trial_status', {
        p_user_id: user.id
      });

      if (error) {
        console.error('Error fetching trial status:', error);
        return null;
      }

      return data as {
        is_on_trial: boolean;
        trial_tier: string | null;
        trial_start_date: string | null;
        trial_end_date: string | null;
        days_remaining: number;
        trial_used_chef_master: boolean;
        trial_used_pro: boolean;
      };
    },
    enabled: !!user?.id && !isOfflineMode,
    refetchInterval: isOfflineMode ? false : 60000, // Check every minute for trial transitions when online
  });

  // Fetch credit balance using new global pool RPC
  const { data: creditBalanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['credit-balance', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase.rpc('get_credit_balance', {
        p_user_id: user.id
      });

      if (error) {
        console.error('Error fetching credit balance:', error);
        return null;
      }

      return data as {
        is_unlimited: boolean;
        tier: string;
        tier_limit: number;
        credits_used: number;
        free_remaining: number;
        purchased_credits: number;
        total_available: number;
        is_trial_credits: boolean;
        trial_credits_expire_at: string | null;
      };
    },
    enabled: !!user?.id && !isOfflineMode,
    refetchInterval: isOfflineMode ? false : 30000, // Refetch every 30 seconds when online
  });

  // Check and transition trial if needed
  useEffect(() => {
    const checkTrialTransition = async () => {
      if (!user?.id) return;
      
      // Only check if trial has ended
      if (trialStatusData?.is_on_trial === false && subscription?.trial_tier) {
        await supabase.rpc('check_and_transition_trial', {
          p_user_id: user.id
        });
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['trial-status'] });
      }
    };

    checkTrialTransition();
  }, [user?.id, trialStatusData, subscription?.trial_tier, queryClient]);

  useEffect(() => {
    if (subscription) {
      setTier(subscription.tier);
      setSubscriptionStatus(subscription.subscription_status);
      setCurrentPeriodEnd(subscription.current_period_end);
      setCancelAtPeriodEnd(subscription.cancel_at_period_end);
      setStripeCustomerId(subscription.stripe_customer_id);
    } else {
      setTier('free');
      setSubscriptionStatus(null);
      setCurrentPeriodEnd(null);
      setCancelAtPeriodEnd(null);
      setStripeCustomerId(null);
    }
  }, [subscription]);

  // Calculate effective tier (considering trial AND offline session)
  // Valid paid subscription statuses: 'active' or 'trialing' (Stripe card trial)
  // ALSO validate that the subscription period hasn't expired
  const isPaidSubscriptionActive = (() => {
    if (tier === 'free') return false;
    if (subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing') return false;
    
    // Check if subscription period has ended
    if (currentPeriodEnd) {
      const periodEndDate = new Date(currentPeriodEnd);
      const now = new Date();
      if (now > periodEndDate) {
        console.log('[SubscriptionContext] Subscription period has ended, treating as inactive');
        return false;
      }
    }
    
    return true;
  })();

  // Determine effective tier - use offline session if offline and valid
  const computedEffectiveTier: 'free' | 'pro' | 'chef_master' = (() => {
    // If offline and we have a valid cached session, use it
    if (isOfflineMode && offlineSession) {
      console.log('[SubscriptionContext] Using cached offline tier:', offlineSession.effectiveTier);
      return offlineSession.effectiveTier;
    }

    // Online logic
    if (subscription?.trial_tier === 'chef_master' && subscription.trial_end_date && new Date(subscription.trial_end_date) > new Date()) return 'chef_master';
    if (isPaidSubscriptionActive) {
      return tier;
    }
    
    if (trialStatusData?.is_on_trial && trialStatusData?.trial_tier) {
      return trialStatusData.trial_tier as 'chef_master' | 'pro';
    }

    // Check family membership
    if (familyMembershipData) {
      return 'chef_master';
    }
    
    return subscription?.halloween_lifetime_pro ? 'pro' : 'free';
  })();

  const effectiveTier = computedEffectiveTier;
  const isFamilyMember = !!familyMembershipData && effectiveTier === 'chef_master' && !isPaidSubscriptionActive;

  // Save offline session when we have online subscription data
  useEffect(() => {
    if (!isOfflineMode && user?.email && effectiveTier !== 'free') {
      const isOnTrial = trialStatusData?.is_on_trial ?? false;
      const trialEndDate = trialStatusData?.trial_end_date ?? null;
      
      saveOfflineSession(
        user.id,
        user.email,
        tier,
        effectiveTier,
        subscriptionStatus,
        currentPeriodEnd,
        trialEndDate,
        isOnTrial
      ).catch(err => {
        console.error('[SubscriptionContext] Failed to save offline session:', err);
      });
    }
  }, [user?.id, user?.email, tier, effectiveTier, subscriptionStatus, currentPeriodEnd, trialStatusData, isOfflineMode]);

  // Clear offline session on logout
  useEffect(() => {
    if (!user?.id && offlineSessionChecked) {
      clearOfflineSession();
      setOfflineSession(null);
    }
  }, [user?.id, offlineSessionChecked]);

  const hasFeature = useCallback((feature: string): boolean => {
    return tierHasFeature(effectiveTier, feature as FeatureKey);
  }, [effectiveTier]);

  const refetchSubscription = useCallback(() => {
    if (!isOfflineMode) {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] });
      queryClient.invalidateQueries({ queryKey: ['trial-status'] });
    }
  }, [isOfflineMode, queryClient]);

  const manualSyncSubscription = async () => {
    if (!user?.id) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('[SubscriptionContext] No session for sync, skipping');
        return;
      }

      const { error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      // If the edge function returns 401 (session expired server-side), 
      // don't throw - the local DB already has subscription data as fallback
      if (error) {
        console.warn('[SubscriptionContext] check-subscription returned error, using local DB fallback:', error.message);
        // Still refetch from local DB to ensure UI is up to date
      }

      // Refetch from local DB regardless
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });
      await queryClient.invalidateQueries({ queryKey: ['credit-balance'] });
      await queryClient.invalidateQueries({ queryKey: ['trial-status'] });
    } catch (error) {
      console.error('Error syncing subscription:', error);
      // Don't throw - allow the app to continue with local DB data
    }
  };

  // Include auth user loading so paid sessions don't briefly look "free" and load AdSense
  const loading = userLoading || (!!user?.id && (subLoading || balanceLoading || trialLoading || familyLoading));

  // Auto-sync if subscription appears stale (period ended but tier still shows paid)
  useEffect(() => {
    const checkStaleSubscription = async () => {
      if (!user?.id || isOfflineMode || loading) return;
      
      // If we have a paid tier but period has ended, force sync with Stripe
      if (tier !== 'free' && currentPeriodEnd) {
        const periodEndDate = new Date(currentPeriodEnd);
        const now = new Date();
        
        if (now > periodEndDate) {
          console.log('[SubscriptionContext] Subscription appears stale (period ended), syncing with Stripe...');
          try {
            await manualSyncSubscription();
          } catch (error) {
            console.error('[SubscriptionContext] Failed to sync stale subscription:', error);
          }
        }
      }
    };
    
    checkStaleSubscription();
  }, [user?.id, tier, currentPeriodEnd, isOfflineMode, loading]);

  // Periodic auto-sync safety net: call check-subscription every 5 minutes
  // This ensures the database stays in sync with Stripe even if webhooks fail
  useEffect(() => {
    if (!user?.id || isOfflineMode) return;

    // Initial sync on login/mount
    manualSyncSubscription().catch(err => {
      console.error('[SubscriptionContext] Initial sync failed:', err);
    });

    const interval = setInterval(() => {
      console.log('[SubscriptionContext] Periodic auto-sync with Stripe');
      manualSyncSubscription().catch(err => {
        console.error('[SubscriptionContext] Periodic sync failed:', err);
      });
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(interval);
  }, [user?.id, isOfflineMode]);

  // Build trial status object
  // User is eligible for Stripe trial if they've never been a paid subscriber
  const isEligibleForTrial = !subscription?.has_been_paid_subscriber && tier === 'free';
  
  // User can try 24-hour no-card trial if they haven't used it yet and are on free tier
  const isOnActiveTrial = trialStatusData?.is_on_trial ?? false;
  const canTry24HourPro = !subscription?.trial_used_pro && tier === 'free' && !isOnActiveTrial;
  const canTry24HourChefMaster = !subscription?.trial_used_chef_master && tier === 'free' && !isOnActiveTrial;
  
  // Check if user is on a Stripe card trial (subscription_status = 'trialing')
  const isOnStripeCardTrial = subscription?.subscription_status === 'trialing' && !!subscription?.current_period_start;
  
  // Calculate days used on Stripe trial
  let stripeTrialDaysUsed = 0;
  if (isOnStripeCardTrial && subscription?.current_period_start) {
    const trialStart = new Date(subscription.current_period_start);
    const now = new Date();
    stripeTrialDaysUsed = Math.floor((now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24));
  }
  
  // Show upgrade prompt for Pro trial users after day 4
  const canUpgradeTrialTier = isOnStripeCardTrial && tier === 'pro' && stripeTrialDaysUsed >= 4;
  
  // When on Stripe card trial, use Stripe dates instead of 24-hour trial dates
  const trialIsOnTrial = isOnStripeCardTrial ? true : isOnActiveTrial;
  const trialTier = isOnStripeCardTrial ? (tier as 'chef_master' | 'pro' | null) : (trialStatusData?.trial_tier as 'chef_master' | 'pro' | null ?? null);
  const trialStartDate = isOnStripeCardTrial ? (subscription?.current_period_start ?? null) : (trialStatusData?.trial_start_date ?? null);
  const trialEndDate = isOnStripeCardTrial ? (currentPeriodEnd ?? null) : (trialStatusData?.trial_end_date ?? null);
  const trialDaysRemaining = isOnStripeCardTrial 
    ? (currentPeriodEnd ? Math.max(0, Math.ceil((new Date(currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0)
    : (trialStatusData?.days_remaining ?? 0);

  const trial: TrialStatus = {
    isOnTrial: trialIsOnTrial,
    trialTier: trialTier,
    trialStartDate: trialStartDate,
    trialEndDate: trialEndDate,
    daysRemaining: trialDaysRemaining,
    trialUsedChefMaster: trialStatusData?.trial_used_chef_master ?? false,
    trialUsedPro: trialStatusData?.trial_used_pro ?? false,
    isEligibleForTrial,
    canTry24HourPro,
    canTry24HourChefMaster,
    isOnStripeCardTrial,
    stripeTrialDaysUsed,
    canUpgradeTrialTier,
  };

  // Build credit balance object - use effective tier for limits
  // Pro trial gets 200 one-time, Pro paid gets 300/month
  const isProTrialCredits = creditBalanceData?.is_trial_credits ?? false;
  const defaultTierLimit = effectiveTier === 'chef_master' ? -1 : (isProTrialCredits ? 200 : (effectiveTier === 'pro' ? 300 : 150));
  
  const creditBalance: CreditBalance = {
    isUnlimited: creditBalanceData?.is_unlimited ?? (effectiveTier === 'chef_master'),
    tier: effectiveTier,
    tierLimit: creditBalanceData?.tier_limit ?? defaultTierLimit,
    creditsUsed: creditBalanceData?.credits_used ?? 0,
    freeRemaining: effectiveTier === 'chef_master' ? -1 : (creditBalanceData?.free_remaining ?? defaultTierLimit),
    purchasedCredits: creditBalanceData?.purchased_credits ?? 0,
    totalAvailable: effectiveTier === 'chef_master' ? -1 : (creditBalanceData?.total_available ?? defaultTierLimit),
    isTrialCredits: isProTrialCredits,
    trialCreditsExpireAt: creditBalanceData?.trial_credits_expire_at ?? null,
  };

  // Legacy support for existing components
  // All logged-in users have no daily limits — only credit-based limits apply
  const remainingUsage = {
    chaosKitchen: {
      daily: Infinity,
      monthly: creditBalance.freeRemaining,
    },
    cursedMode: {
      daily: Infinity,
      monthly: creditBalance.freeRemaining,
    },
    recipeTransform: {
      daily: Infinity,
      monthly: creditBalance.freeRemaining,
    },
    photoAnalysis: {
      daily: Infinity,
      monthly: creditBalance.freeRemaining,
    },
    purchasedCredits: creditBalance.purchasedCredits,
    unlimited: creditBalance.isUnlimited,
  };

  // Track previous tier for cleanup
  const previousEffectiveTierRef = useRef<string | null>(null);

  // Calculate adsAllowed - only free tier sees ads
  const adsAllowed = effectiveTier === 'free';

  // CRITICAL: Cleanup AdSense when user transitions to paid tier
  useEffect(() => {
    const previousTier = previousEffectiveTierRef.current;
    const currentTier = effectiveTier;

    // If transitioning FROM free TO paid, cleanup AdSense immediately
    if (previousTier === 'free' && (currentTier === 'pro' || currentTier === 'chef_master')) {
      console.log('[SubscriptionContext] Tier changed from free to paid - cleaning up AdSense');
      cleanupAdsense();
    }

    // Update previous tier reference
    previousEffectiveTierRef.current = currentTier;
  }, [effectiveTier]);

  const contextValue = useMemo<SubscriptionContextType>(() => ({
    tier,
    effectiveTier,
    loading,
    hasFeature,
    creditBalance,
    remainingUsage,
    refetchSubscription,
    manualSyncSubscription,
    subscriptionStatus,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    stripeCustomerId,
    trial,
    adsAllowed,
    isOfflineMode,
    offlineSessionDaysRemaining,
    isFamilyMember,
  }), [
    tier, effectiveTier, loading, hasFeature, creditBalance, remainingUsage,
    refetchSubscription, manualSyncSubscription, subscriptionStatus,
    currentPeriodEnd, cancelAtPeriodEnd, stripeCustomerId, trial,
    adsAllowed, isOfflineMode, offlineSessionDaysRemaining, isFamilyMember,
  ]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};